import { useEffect, useMemo, useRef, useState } from 'react'
import { currentTheme, THEME_CHANGE_EVENT } from '../../theme/theme'
import type { Theme } from '../../theme/theme'
import { contentFor, localizedName } from '../content'
import {
  layoutFloatingLabels,
  lineLabelAnchor,
} from '../map/lineLabels'
import {
  groupLineDirections,
} from '../map/routeDirections'
import type {
  DirectionVisualRole,
} from '../map/routeDirections'
import { loadGoogleMaps } from '../maps/googleMaps'
import type { Locale, TransportMapLine } from '../types'

const fallbackCenter = { lat: 36.2021, lng: 37.1343 }

const darkMapStyles: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#1d2023' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1d2023' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#a9afb7' }] },
  {
    featureType: 'administrative',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#51565c' }],
  },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#7f858d' }],
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [{ color: '#24272a' }],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#8c929a' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#303438' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#24272a' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#c3c7cc' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#3a3e43' }],
  },
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [{ color: '#262a2e' }],
  },
  {
    featureType: 'transit.station',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#a0a6ae' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#101b24' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#76838d' }],
  },
]

function mapStyles(theme: Theme): google.maps.MapTypeStyle[] {
  return theme === 'dark' ? darkMapStyles : []
}

function lineColor(id: string) {
  let hash = 0

  for (const char of id) {
    hash = ((hash << 5) - hash + char.charCodeAt(0)) | 0
  }

  const hue = Math.abs(hash) % 360

  return 'hsl(' + hue + ' 72% 46%)'
}

function directionRoleLabel(
  role: DirectionVisualRole,
  locale: Locale,
) {
  const labels = contentFor(locale).labels

  if (role === 'outbound') return labels.outbound
  if (role === 'inbound') return labels.inbound
  if (role === 'shared') return labels.sharedRoute

  return labels.direction
}

function directionArrowSymbol(
  color: string,
  opacity: number,
  reverse = false,
  scale = 2.2,
): google.maps.Symbol {
  return {
    path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
    fillColor: color,
    fillOpacity: opacity,
    strokeColor: color,
    strokeOpacity: opacity,
    strokeWeight: 1,
    scale,
    rotation: reverse ? 180 : 0,
  }
}

function routePolylineOptions(
  role: DirectionVisualRole,
  color: string,
  state: 'default' | 'active' | 'muted' = 'default',
): google.maps.PolylineOptions {
  const active = state === 'active'
  const muted = state === 'muted'
  const opacity = muted ? .16 : active ? 1 : .9
  const weight = active ? 8 : 5
  const zIndex = active ? 30 : role === 'inbound' ? 13 : 12
  const arrowOpacity = muted ? .15 : 1
  const arrowScale = active ? 2.7 : 2.2

  if (role === 'inbound') {
    return {
      strokeColor: color,
      strokeOpacity: muted ? .025 : .08,
      strokeWeight: weight,
      zIndex,
      icons: [
        {
          icon: {
            path: 'M 0,-1 0,1',
            strokeColor: color,
            strokeOpacity: opacity,
            strokeWeight: active ? 2.8 : 2.3,
            scale: active ? 3.8 : 3.2,
          },
          offset: '0',
          repeat: '15px',
        },
        {
          icon: directionArrowSymbol(color, arrowOpacity, false, arrowScale),
          offset: '12%',
          repeat: '130px',
        },
      ],
    }
  }

  if (role === 'shared') {
    return {
      strokeColor: color,
      strokeOpacity: opacity,
      strokeWeight: weight,
      zIndex,
      icons: [
        {
          icon: directionArrowSymbol(color, arrowOpacity, false, arrowScale),
          offset: '34%',
        },
        {
          icon: directionArrowSymbol(color, arrowOpacity, true, arrowScale),
          offset: '66%',
        },
      ],
    }
  }

  return {
    strokeColor: color,
    strokeOpacity: opacity,
    strokeWeight: weight,
    zIndex,
    icons: [
      {
        icon: directionArrowSymbol(color, arrowOpacity, false, arrowScale),
        offset: '12%',
        repeat: '130px',
      },
    ],
  }
}

function createDirectionInfoContent(
  lineName: string,
  role: DirectionVisualRole,
  directionNames: string[],
  color: string,
  locale: Locale,
) {
  const wrapper = document.createElement('div')
  wrapper.className = `transport-map-info transport-map-info--line ${locale === 'ar' ? 'is-ar' : 'is-en'}`
  wrapper.dir = locale === 'ar' ? 'rtl' : 'ltr'
  wrapper.style.setProperty('--info-accent', color)

  const title = document.createElement('strong')
  title.className = 'transport-map-info__title'
  title.textContent = lineName

  const roleBadge = document.createElement('div')
  roleBadge.className = `transport-map-info__direction-badge is-${role}`
  roleBadge.textContent = directionRoleLabel(role, locale)

  wrapper.append(title, roleBadge)

  for (const directionName of directionNames) {
    const direction = document.createElement('div')
    direction.className = 'transport-map-info__direction'
    direction.textContent = directionName
    wrapper.append(direction)
  }

  return wrapper
}

interface FloatingLineLabelApi extends google.maps.OverlayView {
  readonly id: string
  readonly element: HTMLButtonElement
  screenPoint: () => { x: number; y: number } | null
  size: () => { width: number; height: number }
  setOffset: (x: number, y: number) => void
  setState: (active: boolean, muted: boolean) => void
}

interface DirectionPolylineVisual {
  polyline: google.maps.Polyline
  role: DirectionVisualRole
}

interface LineVisual {
  id: string
  name: string
  color: string
  bounds: google.maps.LatLngBounds
  polylines: DirectionPolylineVisual[]
  label: FloatingLineLabelApi | null
}

interface LabelCallbacks {
  onEnter: () => void
  onLeave: () => void
  onClick: () => void
}

function createFloatingLineLabelClass() {
  return class FloatingLineLabel
    extends google.maps.OverlayView
    implements FloatingLineLabelApi {
    readonly id: string
    readonly element: HTMLButtonElement

    private readonly anchor: google.maps.LatLng
    private offsetX = 0
    private offsetY = -30

    constructor(
      id: string,
      anchor: google.maps.LatLng,
      name: string,
      color: string,
      locale: Locale,
      order: number,
      callbacks: LabelCallbacks,
    ) {
      super()

      this.id = id
      this.anchor = anchor

      const element = document.createElement('button')
      element.type = 'button'
      element.className = `transport-line-label ${locale === 'ar' ? 'is-ar' : 'is-en'}`
      element.dir = locale === 'ar' ? 'rtl' : 'ltr'
      element.title = name
      element.setAttribute('aria-label', name)
      element.style.setProperty('--line-color', color)
      element.style.setProperty('--line-label-delay', `${Math.min(order * 32, 320)}ms`)

      const accent = document.createElement('span')
      accent.className = 'transport-line-label__accent'
      accent.setAttribute('aria-hidden', 'true')

      const text = document.createElement('span')
      text.className = 'transport-line-label__name'
      text.textContent = name

      element.append(accent, text)

      element.addEventListener('mouseenter', callbacks.onEnter)
      element.addEventListener('mouseleave', callbacks.onLeave)
      element.addEventListener('focus', callbacks.onEnter)
      element.addEventListener('blur', callbacks.onLeave)
      element.addEventListener('click', callbacks.onClick)

      this.element = element
    }

    override onAdd() {
      const pane = this.getPanes()?.overlayMouseTarget
      pane?.appendChild(this.element)
      google.maps.OverlayView.preventMapHitsAndGesturesFrom(this.element)
    }

    override draw() {
      const projection = this.getProjection()
      const point = projection?.fromLatLngToDivPixel(this.anchor)

      if (!point) return

      this.element.style.left = `${point.x + this.offsetX}px`
      this.element.style.top = `${point.y + this.offsetY}px`
    }

    override onRemove() {
      this.element.remove()
    }

    screenPoint() {
      const projection = this.getProjection()
      const point = projection?.fromLatLngToDivPixel(this.anchor)

      return point ? { x: point.x, y: point.y } : null
    }

    size() {
      return {
        width: Math.max(this.element.offsetWidth, 112),
        height: Math.max(this.element.offsetHeight, 34),
      }
    }

    setOffset(x: number, y: number) {
      this.offsetX = x
      this.offsetY = y
      this.draw()
    }

    setState(active: boolean, muted: boolean) {
      this.element.classList.toggle('is-active', active)
      this.element.classList.toggle('is-muted', muted)
    }
  }
}

export function TransportNetworkMap({
  locale,
  lines,
}: {
  locale: Locale
  lines: TransportMapLine[]
}) {
  const container = useRef<HTMLDivElement | null>(null)
  const [failed, setFailed] = useState(false)
  const lineColors = useMemo(
    () => new Map(lines.map((line) => [line.id, lineColor(line.id)])),
    [lines],
  )
  const directionGroupsByLine = useMemo(
    () => new Map(
      lines.map((line) => [line.id, groupLineDirections(line.directions)]),
    ),
    [lines],
  )
  const legendRoles = useMemo(() => {
    const roles = new Set<DirectionVisualRole>()

    for (const line of lines) {
      if (line.directions.length < 2) continue

      for (const group of directionGroupsByLine.get(line.id) ?? []) {
        roles.add(group.role)
      }
    }

    return roles
  }, [directionGroupsByLine, lines])

  useEffect(() => {
    let disposed = false
    let map: google.maps.Map | null = null
    let relayoutFrame: number | null = null
    let idleListener: google.maps.MapsEventListener | null = null
    const polylines: google.maps.Polyline[] = []
    const markers: google.maps.Marker[] = []
    const labels: FloatingLineLabelApi[] = []
    const lineVisuals = new Map<string, LineVisual>()
    let info: google.maps.InfoWindow | null = null

    const applyLineFocus = (lineId: string | null) => {
      for (const visual of lineVisuals.values()) {
        const active = lineId === visual.id
        const muted = lineId !== null && !active

        for (const route of visual.polylines) {
          route.polyline.setOptions(routePolylineOptions(
            route.role,
            visual.color,
            muted ? 'muted' : active ? 'active' : 'default',
          ))
        }

        visual.label?.setState(active, muted)
      }
    }

    const layoutLabels = () => {
      if (relayoutFrame != null) cancelAnimationFrame(relayoutFrame)

      relayoutFrame = requestAnimationFrame(() => {
        const items = labels.flatMap((label) => {
          const point = label.screenPoint()

          if (!point) return []

          const size = label.size()

          return [{
            id: label.id,
            x: point.x,
            y: point.y,
            width: size.width,
            height: size.height,
          }]
        })

        const offsets = layoutFloatingLabels(items)

        for (const label of labels) {
          const offset = offsets.get(label.id)
          if (offset) label.setOffset(offset.x, offset.y)
        }
      })
    }

    const handleThemeChange = () => {
      map?.setOptions({ styles: mapStyles(currentTheme()) })
    }

    window.addEventListener(THEME_CHANGE_EVENT, handleThemeChange)
    window.addEventListener('resize', layoutLabels)

    void loadGoogleMaps().then(() => {
      if (disposed || !container.current) return
      setFailed(false)

      const FloatingLineLabel = createFloatingLineLabelClass()

      map = new google.maps.Map(container.current, {
        center: fallbackCenter,
        zoom: 12,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        clickableIcons: false,
        gestureHandling: 'greedy',
        styles: mapStyles(currentTheme()),
      })

      info = new google.maps.InfoWindow()
      const bounds = new google.maps.LatLngBounds()
      let hasPosition = false

      const stopIndex = new Map<string, {
        id: string
        code: string
        nameAr: string
        nameEn: string | null
        lat: number
        lng: number
        lines: Map<string, string>
      }>()

      for (const [lineIndex, line] of lines.entries()) {
        const color = lineColors.get(line.id) ?? '#b72025'
        const lineName = localizedName(line, locale) || line.code
        const lineBounds = new google.maps.LatLngBounds()
        const visual: LineVisual = {
          id: line.id,
          name: lineName,
          color,
          bounds: lineBounds,
          polylines: [],
          label: null,
        }
        lineVisuals.set(line.id, visual)

        for (const direction of line.directions) {
          for (const stop of direction.stops) {
            if (stop.latitude == null || stop.longitude == null) continue

            const lat = Number(stop.latitude)
            const lng = Number(stop.longitude)
            bounds.extend({ lat, lng })
            lineBounds.extend({ lat, lng })
            hasPosition = true

            const existing = stopIndex.get(stop.id)

            if (existing) {
              existing.lines.set(line.id, lineName)
            } else {
              stopIndex.set(stop.id, {
                id: stop.id,
                code: stop.code,
                nameAr: stop.nameAr,
                nameEn: stop.nameEn,
                lat,
                lng,
                lines: new Map([[line.id, lineName]]),
              })
            }
          }
        }

        const routeGroups = directionGroupsByLine.get(line.id) ?? []

        for (const routeGroup of routeGroups) {
          const path = routeGroup.geometry.map((point) => ({
            lat: point.latitude,
            lng: point.longitude,
          }))

          if (!path.length) continue

          path.forEach((point) => {
            bounds.extend(point)
            lineBounds.extend(point)
            hasPosition = true
          })

          const polyline = new google.maps.Polyline({
            map,
            path,
            clickable: true,
            geodesic: false,
            ...routePolylineOptions(routeGroup.role, color),
          })
          polylines.push(polyline)
          visual.polylines.push({
            polyline,
            role: routeGroup.role,
          })

          polyline.addListener('mouseover', () => {
            applyLineFocus(line.id)
          })
          polyline.addListener('mouseout', () => {
            applyLineFocus(null)
          })
          polyline.addListener('click', (event: google.maps.PolyMouseEvent) => {
            if (!event.latLng || !info || !map) return

            const directionNames = routeGroup.directions.map((direction) =>
              localizedName(direction, locale) || direction.code
            )

            info.setContent(createDirectionInfoContent(
              lineName,
              routeGroup.role,
              directionNames,
              color,
              locale,
            ))
            info.setPosition(event.latLng)
            info.open({ map })
          })
        }

        const anchor = lineLabelAnchor(line)

        if (anchor) {
          const label = new FloatingLineLabel(
            line.id,
            new google.maps.LatLng(anchor.latitude, anchor.longitude),
            lineName,
            color,
            locale,
            lineIndex,
            {
              onEnter: () => applyLineFocus(line.id),
              onLeave: () => applyLineFocus(null),
              onClick: () => {
                if (!map || lineBounds.isEmpty()) return

                map.fitBounds(lineBounds, {
                  top: 96,
                  right: 72,
                  bottom: 96,
                  left: 72,
                })
              },
            },
          )

          label.setMap(map)
          labels.push(label)
          visual.label = label
        }
      }

      for (const stop of stopIndex.values()) {
        const marker = new google.maps.Marker({
          map,
          position: { lat: stop.lat, lng: stop.lng },
          title: localizedName(stop, locale) || stop.code,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: '#ffffff',
            fillOpacity: 1,
            strokeColor: '#b72025',
            strokeOpacity: 1,
            strokeWeight: 2,
            scale: 5,
          },
          zIndex: 20,
        })

        marker.addListener('click', () => {
          if (!info || !map) return

          const wrapper = document.createElement('div')
          wrapper.className = `transport-map-info transport-map-info--stop ${locale === 'ar' ? 'is-ar' : 'is-en'}`
          wrapper.dir = locale === 'ar' ? 'rtl' : 'ltr'
          wrapper.style.setProperty('--info-accent', '#b72025')

          const title = document.createElement('strong')
          title.className = 'transport-map-info__title'
          title.textContent = localizedName(stop, locale) || stop.code

          const code = document.createElement('div')
          code.className = 'transport-map-info__meta'
          code.textContent = stop.code

          const serving = document.createElement('div')
          serving.className = 'transport-map-info__lines'
          serving.textContent = [...stop.lines.values()].join(' • ')

          wrapper.append(title, code, serving)

          info.setContent(wrapper)
          info.open({ map, anchor: marker })
        })
        markers.push(marker)
      }

      if (hasPosition && !bounds.isEmpty()) {
        map.fitBounds(bounds, 48)
      } else {
        map.setCenter(fallbackCenter)
        map.setZoom(12)
      }

      idleListener = map.addListener('idle', layoutLabels)
      requestAnimationFrame(layoutLabels)
    }).catch(() => {
      if (!disposed) setFailed(true)
    })

    return () => {
      disposed = true
      window.removeEventListener(THEME_CHANGE_EVENT, handleThemeChange)
      window.removeEventListener('resize', layoutLabels)

      if (relayoutFrame != null) cancelAnimationFrame(relayoutFrame)
      idleListener?.remove()

      for (const label of labels) {
        label.setMap(null)
      }
      for (const polyline of polylines) {
        google.maps.event.clearInstanceListeners(polyline)
        polyline.setMap(null)
      }
      for (const marker of markers) {
        google.maps.event.clearInstanceListeners(marker)
        marker.setMap(null)
      }

      info?.close()
    }
  }, [directionGroupsByLine, lineColors, lines, locale])

  if (failed) {
    return (
      <div className="map-app__fallback">
        {contentFor(locale).labels.mapUnavailable}
      </div>
    )
  }

  const legendOrder: DirectionVisualRole[] = ['outbound', 'inbound', 'shared']
  const visibleLegendRoles = legendOrder.filter((role) => legendRoles.has(role))

  return (
    <>
      <div
        ref={container}
        className="map-app__map"
        aria-label={locale === 'ar' ? 'خريطة خطوط الباصات' : 'Bus lines map'}
      />

      {visibleLegendRoles.length > 0 && (
        <div
          className="transport-direction-legend"
          aria-label={locale === 'ar' ? 'دليل اتجاهات الخطوط' : 'Route direction legend'}
        >
          {visibleLegendRoles.map((role) => (
            <div className="transport-direction-legend__item" key={role}>
              <span
                className={`transport-direction-legend__sample is-${role}`}
                aria-hidden="true"
              />
              <span>{directionRoleLabel(role, locale)}</span>
            </div>
          ))}
        </div>
      )}
    </>
  )
}
